import { useEffect, useMemo, useState } from 'react'
import { Check, RotateCcw, TriangleAlert } from 'lucide-react'
import Button from '../../components/Button'
import ColorField from '../../components/ColorField'
import FormRow from '../../components/FormRow'
import { contrastRatio, normalizeHex } from '../../components/colorUtils'
import { useToast } from '../../components/toastContext'
import {
  DEFAULT_ACTIVE,
  DEFAULT_MAIN,
  applyBranding,
  railColor,
  railMark,
  readBranding,
  saveBranding,
} from '../../app/branding'
import { saveSection } from './settingsService'
import { common as C, settingsStrings as S } from './strings'
import './BrandThemeCard.css'

const T = S.theme

/** AA: 4.5 for text, 3 for a UI graphic. */
const check = (a, b, floor) => {
  const ratio = contrastRatio(a, b)
  if (ratio == null) return null
  return { ratio: Math.round(ratio * 10) / 10, pass: ratio >= floor }
}

/**
 * الهوية البصرية — the client identity pair.
 *
 * tokens.css is built around `--gov-primary` and `--gov-gold` as "two knobs
 * [that] rebrand the whole app"; this is the screen that turns that into a
 * setting. Writing the pair onto :root re-points every accent, rail, ring and
 * chip at once — no stylesheet knows the feature exists.
 *
 * Two fields, two contrast figures, one preview. An earlier pass also offered
 * six curated presets, which made a two-field decision the largest thing in
 * Settings; picking a colour does not need a gallery.
 *
 * The contrast figures are always on screen rather than appearing once a choice
 * has gone wrong — a warning that only shows up after the fact teaches nothing
 * about what "good" would have been.
 */
export default function BrandThemeCard({ initialBrand }) {
  const toast = useToast()
  const [saved, setSaved] = useState(() => ({
    main: normalizeHex(initialBrand?.main) || DEFAULT_MAIN,
    active: normalizeHex(initialBrand?.active) || DEFAULT_ACTIVE,
  }))
  const [draft, setDraft] = useState(saved)
  const [saving, setSaving] = useState(false)

  /* The draft paints the live app as you try it, so the whole product is the
     preview and the miniature is only its summary. Leaving without saving
     restores what was stored. */
  useEffect(() => {
    applyBranding(draft)
    return () => applyBranding(readBranding())
  }, [draft])

  const main = normalizeHex(draft.main) || DEFAULT_MAIN
  const active = normalizeHex(draft.active) || DEFAULT_ACTIVE
  const rail = useMemo(() => railColor(main), [main])

  const dirty = draft.main !== saved.main || draft.active !== saved.active
  const valid = Boolean(normalizeHex(draft.main) && normalizeHex(draft.active))
  const isDefault = main === DEFAULT_MAIN && active === DEFAULT_ACTIVE

  const set = (key) => (value) => setDraft((previous) => ({ ...previous, [key]: value }))

  const submit = async () => {
    const brand = { main, active }
    setSaving(true)
    const outcome = await saveSection('brandTheme', brand)
    setSaving(false)

    if (!outcome.ok) {
      toast.error(outcome.message || C.saveFailed)
      return
    }

    saveBranding(brand)
    setSaved(brand)
    setDraft(brand)
    toast.success(outcome.message || T.saved)
  }

  return (
    <section className="brand-theme">
      <header className="brand-theme__head">
        <div className="brand-theme__titles">
          <h3 className="brand-theme__title">{T.title}</h3>
          <p className="brand-theme__hint">{T.hint}</p>
        </div>
        <div className="brand-theme__actions">
          <Button
            variant="ghost"
            size="sm"
            disabled={isDefault}
            onClick={() => setDraft({ main: DEFAULT_MAIN, active: DEFAULT_ACTIVE })}
          >
            <RotateCcw size={15} strokeWidth={1.75} />
            {T.restore}
          </Button>
          <Button size="sm" disabled={!dirty || !valid || saving} onClick={submit}>
            {T.save}
          </Button>
        </div>
      </header>

      <div className="brand-theme__body">
        <div className="brand-theme__controls">
          <div className="brand-theme__pair">
            <FormRow label={T.main} hint={T.mainHint}>
              {(id) => (
                <ColorField
                  id={id}
                  value={draft.main}
                  placeholder={DEFAULT_MAIN}
                  onChange={set('main')}
                />
              )}
            </FormRow>

            <FormRow label={T.active} hint={T.activeHint}>
              {(id) => (
                <ColorField
                  id={id}
                  value={draft.active}
                  placeholder={DEFAULT_ACTIVE}
                  onChange={set('active')}
                />
              )}
            </FormRow>
          </div>

          <div className="brand-theme__checks">
            <Readout label={T.mainContrast} result={check(main, '#ffffff', 4.5)} />
            {/* The rail lightens the gold before it draws it, so that is what
                gets measured — see railMark. */}
            <Readout label={T.activeContrast} result={check(railMark(active), rail, 3)} />
          </div>
        </div>

        {/* A miniature of THIS app, painted in the draft pair. */}
        <figure className="brand-theme__preview">
          <div
            className="brand-mini"
            style={{ '--mini-main': main, '--mini-active': active, '--mini-rail': rail }}
            aria-hidden="true"
          >
            <span className="brand-mini__rail">
              <i className="brand-mini__logo" />
              <i className="brand-mini__link" />
              <i className="brand-mini__link brand-mini__link--on" />
              <i className="brand-mini__link" />
            </span>
            <span className="brand-mini__page">
              <span className="brand-mini__header">
                <i className="brand-mini__emblem" />
                <i className="brand-mini__title" />
              </span>
              <span className="brand-mini__row">
                <i className="brand-mini__btn" />
                <i className="brand-mini__chip" />
              </span>
              <span className="brand-mini__card" />
            </span>
          </div>
          <figcaption className="brand-theme__caption">{T.preview}</figcaption>
        </figure>
      </div>
    </section>
  )
}

/** One contrast figure: the ratio, and whether it clears its floor. */
function Readout({ label, result }) {
  if (!result) return null
  return (
    <p className={`brand-check${result.pass ? ' is-pass' : ' is-weak'}`}>
      {result.pass ? (
        <Check size={13} strokeWidth={2.5} aria-hidden="true" />
      ) : (
        <TriangleAlert size={13} strokeWidth={2} aria-hidden="true" />
      )}
      <bdi className="brand-check__label">{label}</bdi>
      <span className="brand-check__ratio" dir="ltr">
        {result.ratio}:1
      </span>
      <span className="brand-check__verdict">{result.pass ? T.pass : T.weak}</span>
    </p>
  )
}
